import {
    Component,
    EventEmitter,
    Input,
    OnDestroy,
    OnInit,
    Output,
    ViewEncapsulation,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    ElementRef,
    ViewChild,
    HostListener,
} from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { FormControl } from '@angular/forms';
import { Font } from '../../shared/models/font.model';
import { Subject, debounceTime, distinctUntilChanged, takeUntil } from 'rxjs';

interface GoogleFontItem {
    family: string;
    variants: string[];
    files: Record<string, string>;
    category: string;
}

/**
 * Custom Google Font Picker component that replaces ngx-font-picker.
 *
 * Features:
 *  - Loads fonts from the official Google Fonts API
 *  - Searchable dropdown with live font preview
 *  - Font style (weight/italic) selector
 *  - Font size input
 *  - Preset fonts support
 *  - Emits the same Font object shape for DB compatibility
 */
@Component({
    selector: 'app-google-font-picker',
    templateUrl: './google-font-picker.component.html',
    styleUrls: ['./google-font-picker.component.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GoogleFontPickerComponent implements OnInit, OnDestroy {
    /** Current font (two-way binding) */
    @Input() font!: Font;

    /** Preset font family names shown at top */
    @Input() presetFonts: string[] = [];

    /** Label shown above presets */
    @Input() presetLabel: string = 'Preset fonts';

    /** Google Fonts API key */
    @Input() apiKey: string = '';

    @Output() fontChange = new EventEmitter<Font>();

    @ViewChild('dropdownPanel') dropdownPanel!: ElementRef;
    @ViewChild('triggerButton') triggerButton!: ElementRef;
    @ViewChild('searchInput') searchInput!: ElementRef;

    searchControl = new FormControl('');
    sizeControl!: FormControl;

    allFonts: GoogleFontItem[] = [];
    filteredFonts: GoogleFontItem[] = [];
    presetFontItems: GoogleFontItem[] = [];
    displayedFonts: GoogleFontItem[] = [];

    isOpen = false;
    loading = false;
    selectedFont: GoogleFontItem | null = null;

    /** Virtual scroll batch size */
    private batchSize = 40;
    private displayedCount = 40;

    private destroy$ = new Subject<void>();
    private fontsLoaded = false;
    private loadedFontFamilies = new Set<string>();

    /**
     * System / web-safe fonts that every browser ships with.
     * These are NOT in the Google Fonts API, so we create
     * synthetic GoogleFontItem entries for them.
     */
    private readonly systemFonts: Record<string, GoogleFontItem> = {
        arial: {
            family: 'Arial',
            variants: ['regular', 'italic', '700', '700italic'],
            files: {},
            category: 'sans-serif',
        },
        verdana: {
            family: 'Verdana',
            variants: ['regular', 'italic', '700', '700italic'],
            files: {},
            category: 'sans-serif',
        },
        tahoma: {
            family: 'Tahoma',
            variants: ['regular', '700'],
            files: {},
            category: 'sans-serif',
        },
        'trebuchet ms': {
            family: 'Trebuchet MS',
            variants: ['regular', 'italic', '700', '700italic'],
            files: {},
            category: 'sans-serif',
        },
        georgia: {
            family: 'Georgia',
            variants: ['regular', 'italic', '700', '700italic'],
            files: {},
            category: 'serif',
        },
        'times new roman': {
            family: 'Times New Roman',
            variants: ['regular', 'italic', '700', '700italic'],
            files: {},
            category: 'serif',
        },
        'courier new': {
            family: 'Courier New',
            variants: ['regular', 'italic', '700', '700italic'],
            files: {},
            category: 'monospace',
        },
        helvetica: {
            family: 'Helvetica',
            variants: ['regular', 'italic', '700', '700italic'],
            files: {},
            category: 'sans-serif',
        },
        'lucida console': {
            family: 'Lucida Console',
            variants: ['regular'],
            files: {},
            category: 'monospace',
        },
        impact: {
            family: 'Impact',
            variants: ['regular'],
            files: {},
            category: 'sans-serif',
        },
        'comic sans ms': {
            family: 'Comic Sans MS',
            variants: ['regular', '700'],
            files: {},
            category: 'handwriting',
        },
        candara: {
            family: 'Candara',
            variants: ['regular', 'italic', '700', '700italic'],
            files: {},
            category: 'sans-serif',
        },
    };

    /** Style lookup for display labels */
    private readonly styleLookup: Record<string, string> = {
        '100': 'Thin',
        '100italic': 'Thin Italic',
        '200': 'Extra-Light',
        '200italic': 'Extra-Light Italic',
        '300': 'Light',
        '300italic': 'Light Italic',
        regular: 'Regular',
        italic: 'Italic',
        '400': 'Regular',
        '400italic': 'Italic',
        '500': 'Medium',
        '500italic': 'Medium Italic',
        '600': 'Semi-Bold',
        '600italic': 'Semi-Bold Italic',
        '700': 'Bold',
        '700italic': 'Bold Italic',
        '800': 'Extra-Bold',
        '800italic': 'Extra-Bold Italic',
        '900': 'Black',
        '900italic': 'Black Italic',
    };

    constructor(
        private http: HttpClient,
        private cd: ChangeDetectorRef,
        private elRef: ElementRef,
    ) {}

    ngOnInit(): void {
        const currentSize = this.font?.size
            ? parseInt(this.font.size.replace(/[^0-9]/g, '') || '16', 10)
            : 16;
        this.sizeControl = new FormControl(currentSize);

        this.sizeControl.valueChanges
            .pipe(
                debounceTime(400),
                distinctUntilChanged(),
                takeUntil(this.destroy$),
            )
            .subscribe((val) => {
                if (val && val > 0 && val <= 200) {
                    this.font.size = val + 'px';
                    this.emitChange();
                }
            });

        this.searchControl.valueChanges
            .pipe(
                debounceTime(250),
                distinctUntilChanged(),
                takeUntil(this.destroy$),
            )
            .subscribe((term) => {
                this.filterFonts(term || '');
            });
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    /** Close when clicking outside */
    @HostListener('document:click', ['$event'])
    onDocumentClick(event: MouseEvent): void {
        if (this.isOpen && !this.elRef.nativeElement.contains(event.target)) {
            this.isOpen = false;
            this.cd.markForCheck();
        }
    }

    /** Toggle the picker dropdown */
    toggleDropdown(): void {
        this.isOpen = !this.isOpen;
        if (this.isOpen) {
            if (!this.fontsLoaded) {
                this.loadFonts();
            }
            // Focus the search input after the dropdown renders
            setTimeout(() => {
                this.searchInput?.nativeElement?.focus();
            }, 50);
        }
        this.cd.markForCheck();
    }

    /** Fetch fonts from Google Fonts API */
    private loadFonts(): void {
        this.loading = true;
        this.cd.markForCheck();

        const url = `https://www.googleapis.com/webfonts/v1/webfonts?key=${this.apiKey}&sort=popularity`;
        this.http.get<{ items: GoogleFontItem[] }>(url).subscribe({
            next: (response) => {
                const apiFonts = response.items || [];

                // Merge system/web-safe fonts at the top so they
                // appear in search results and the "All Fonts" list
                const systemEntries = Object.values(this.systemFonts).filter(
                    (sf) =>
                        !apiFonts.some(
                            (af) =>
                                af.family.toLowerCase() ===
                                sf.family.toLowerCase(),
                        ),
                );
                this.allFonts = [...systemEntries, ...apiFonts];

                this.fontsLoaded = true;
                this.loading = false;

                // Build preset items — fall back to system fonts for web-safe families
                this.presetFontItems = this.presetFonts
                    .map((name) => {
                        const key = name.toLowerCase();
                        return (
                            this.allFonts.find(
                                (f) => f.family.toLowerCase() === key,
                            ) ||
                            this.systemFonts[key] ||
                            null
                        );
                    })
                    .filter((f): f is GoogleFontItem => !!f);

                // Also find current selection in system fonts if not in API
                if (this.font?.family) {
                    const familyKey = this.font.family.toLowerCase();
                    this.selectedFont =
                        this.allFonts.find(
                            (f) => f.family.toLowerCase() === familyKey,
                        ) ||
                        this.systemFonts[familyKey] ||
                        null;
                }

                this.filterFonts(this.searchControl.value || '');
                this.cd.markForCheck();
            },
            error: () => {
                this.loading = false;
                this.cd.markForCheck();
            },
        });
    }

    /** Filter the font list by search term */
    private filterFonts(term: string): void {
        const query = term.toLowerCase().trim();
        if (!query) {
            this.filteredFonts = this.allFonts;
        } else {
            this.filteredFonts = this.allFonts.filter((f) =>
                f.family.toLowerCase().includes(query),
            );
        }

        this.displayedCount = this.batchSize;
        this.displayedFonts = this.filteredFonts.slice(0, this.displayedCount);

        // Preload preview fonts for visible items
        this.loadPreviewFonts(this.displayedFonts);
        this.cd.markForCheck();
    }

    /** Load more fonts when scrolling */
    onScroll(event: Event): void {
        const el = event.target as HTMLElement;
        if (el.scrollTop + el.clientHeight >= el.scrollHeight - 50) {
            this.loadMore();
        }
    }

    /** Load next batch */
    private loadMore(): void {
        if (this.displayedCount >= this.filteredFonts.length) return;

        const start = this.displayedCount;
        this.displayedCount = Math.min(
            this.displayedCount + this.batchSize,
            this.filteredFonts.length,
        );
        const newItems = this.filteredFonts.slice(start, this.displayedCount);
        this.displayedFonts = [...this.displayedFonts, ...newItems];

        this.loadPreviewFonts(newItems);
        this.cd.markForCheck();
    }

    /** Dynamically load Google Font stylesheet for preview */
    private loadPreviewFonts(fonts: GoogleFontItem[]): void {
        const families = fonts
            .filter(
                (f) =>
                    !this.loadedFontFamilies.has(f.family) &&
                    !this.systemFonts[f.family.toLowerCase()],
            )
            .map((f) => f.family.replace(/ /g, '+'));

        if (families.length === 0) return;

        // Batch in groups of 10 to avoid URL length issues
        for (let i = 0; i < families.length; i += 10) {
            const batch = families.slice(i, i + 10);
            const link = document.createElement('link');
            link.rel = 'stylesheet';
            link.href = `https://fonts.googleapis.com/css?family=${batch.join('|')}&display=swap`;
            document.head.appendChild(link);
        }

        fonts.forEach((f) => this.loadedFontFamilies.add(f.family));
    }

    /** Select a font family from the list */
    selectFontFamily(item: GoogleFontItem): void {
        this.selectedFont = item;
        this.font.family = item.family;
        this.font.files = item.files || null;
        this.font.styles = item.variants || ['regular'];

        // Reset style to regular if available, else first variant
        if (item.variants.includes('regular')) {
            this.font.style = 'regular';
        } else if (item.variants.length > 0) {
            this.font.style = item.variants[0];
        }

        // Load the full font with selected variant
        this.loadSelectedFontVariant();
        this.emitChange();
        this.cd.markForCheck();
    }

    /** Change font style (weight variant) */
    onStyleChange(style: string): void {
        this.font.style = style;
        this.loadSelectedFontVariant();
        this.emitChange();
    }

    /** Get display label for a variant */
    getStyleLabel(variant: string): string {
        return this.styleLookup[variant] || variant;
    }

    /** Get current style display label */
    get currentStyleLabel(): string {
        return this.getStyleLabel(this.font?.style || 'regular');
    }

    /** Load font variant for the selected font (skip for system fonts) */
    private loadSelectedFontVariant(): void {
        if (!this.font?.family) return;

        // System fonts are already available — no stylesheet needed
        if (this.systemFonts[this.font.family.toLowerCase()]) return;

        const family = this.font.family.replace(/ /g, '+');
        let weight = '400';
        let italic = '';

        const style = this.font.style || 'regular';
        if (style === 'italic') {
            weight = '400';
            italic = 'ital,';
        } else if (style === 'regular') {
            weight = '400';
        } else {
            const numMatch = style.match(/(\d+)/);
            weight = numMatch ? numMatch[1] : '400';
            if (style.includes('italic')) {
                italic = 'ital,';
            }
        }

        const link = document.createElement('link');
        link.rel = 'stylesheet';
        if (italic) {
            link.href = `https://fonts.googleapis.com/css2?family=${family}:${italic}wght@1,${weight}&display=swap`;
        } else {
            link.href = `https://fonts.googleapis.com/css2?family=${family}:wght@${weight}&display=swap`;
        }
        document.head.appendChild(link);
    }

    /** Check if a font is selected */
    isFontSelected(item: GoogleFontItem): boolean {
        return this.font?.family?.toLowerCase() === item.family.toLowerCase();
    }

    /** Font preview inline style for list items */
    getFontPreviewStyle(item: GoogleFontItem): Record<string, string> {
        return {
            'font-family': `'${item.family}', ${item.category || 'sans-serif'}`,
        };
    }

    private emitChange(): void {
        // Emit a new Font instance so change detection picks it up everywhere
        this.fontChange.emit(
            new Font({
                family: this.font.family,
                size: this.font.size,
                style: this.font.style,
                files: this.font.files,
                styles: this.font.styles,
            }),
        );
    }

    /** Trackby for ngFor performance */
    trackByFamily(_index: number, item: GoogleFontItem): string {
        return item.family;
    }
}
